/**
 * Daily puzzle generator.
 * Per GAME-SPEC §1.3 + §2.1 difficulty scoring.
 *
 * Algorithm:
 *  1. Build connected graph topology (spanning tree + extra edges)
 *  2. Adjust edges so degree-odd count ∈ {0, 2} → guarantees Euler trail
 *  3. Layout vertices via Fruchterman-Reingold force-directed
 *     - Repulsion (all pairs) pushes vertices apart
 *     - Attraction (only along edges) keeps connected nodes together
 *     - Result: edges crossing is dramatically reduced; layout looks "designed"
 *  4. Pick rarity by difficulty score
 *
 * Hard invariant (GS-2): every returned puzzle has a valid Euler trail.
 */
import { adjustForEulerTrail, findEulerTrail, isConnected, randomVertexPair } from './euler';
import { pickInt, seededRng, shuffle } from './rng';
import type { Edge, Puzzle, Rarity, Vertex } from './types';

// Vertex count range tuned for daily play readability.
// V > 12 starts looking dense even with great layout; keep it tight.
const VERTEX_MIN = 6;
const VERTEX_MAX = 12;
// Layout iterations for force-directed relaxation.
const LAYOUT_ITER = 100;

export function generateDailyPuzzle(utcDate: string): Puzzle {
  const rng = seededRng(utcDate);
  const V = pickInt(rng, VERTEX_MIN, VERTEX_MAX);

  // 1. Topology first (no positions yet — pure graph).
  let edges = buildGraphTopology(V, rng);
  edges = adjustForEulerTrail(edges, V, rng);

  // Safety: should already be connected, but verify.
  if (!isConnected(edges, V)) {
    edges = fallbackGraph(V);
  }

  // 2. Layout via force-directed relaxation seeded by the date.
  const vertices = forceDirectedLayout(V, edges, rng);

  const reference = findEulerTrail(edges, V) ?? undefined;
  const rarity = rarityOf(diffScore(vertices, edges));

  return { seed: utcDate, vertices, edges, rarity, ...(reference ? { reference } : {}) };
}

export function todaySeed(now: Date = new Date()): string {
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Build connected graph topology — spanning tree first (guarantees connectivity)
 * then sparingly add extra edges so the puzzle isn't a boring path.
 * Edge count target: V to floor(1.4*V) — sparse enough to avoid visual chaos.
 */
function buildGraphTopology(V: number, rng: () => number): Edge[] {
  const edges: Edge[] = [];
  let id = 0;

  // Spanning tree by random parent attachment.
  const order = shuffle(rng, Array.from({ length: V }, (_, i) => i));
  for (let i = 1; i < order.length; i++) {
    const a = order[i]!;
    const b = order[pickInt(rng, 0, i - 1)]!;
    edges.push({ id: id++, a, b });
  }

  // Extra edges — capped at floor(V * 0.4) to keep density low.
  // Total E ∈ [V-1, V-1 + V*0.4] ≈ [V-1, 1.4V].
  const extraCap = Math.floor(V * 0.4);
  const targetExtra = pickInt(rng, 0, extraCap);
  const existing = new Set(edges.map((e) => keyOf(e.a, e.b)));
  let attempts = 0;
  while (edges.length < V - 1 + targetExtra && attempts < V * 4) {
    const [a, b] = randomVertexPair(V, rng);
    const k = keyOf(a, b);
    if (!existing.has(k)) {
      existing.add(k);
      edges.push({ id: id++, a, b });
    }
    attempts++;
  }
  return edges;
}

/**
 * Fruchterman-Reingold force-directed layout.
 *
 *  - Initial: vertices on circle (not spiral — spreads them evenly)
 *  - Per iteration:
 *      • Repulsion: all pairs i,j → push apart with force k²/dist
 *      • Attraction: along each edge → pull together with force dist²/k
 *      • Temperature schedule cools displacement over time
 *  - Final clamp to [0.08, 0.92] so vertices stay clear of canvas edges
 */
function forceDirectedLayout(V: number, edges: Edge[], rng: () => number): Vertex[] {
  // Initial layout — even circle with small jitter for seed variation.
  const vertices: Vertex[] = [];
  for (let i = 0; i < V; i++) {
    const theta = (i / V) * Math.PI * 2 + (rng() - 0.5) * 0.3;
    const r = 0.35 + (rng() - 0.5) * 0.04;
    vertices.push({
      id: i,
      x: 0.5 + r * Math.cos(theta),
      y: 0.5 + r * Math.sin(theta),
    });
  }

  // Optimal spring length k = sqrt(area / V) — heuristic from FR paper.
  const k = Math.sqrt(0.7 / V);
  const initialTemp = 0.08;

  for (let iter = 0; iter < LAYOUT_ITER; iter++) {
    const temp = initialTemp * (1 - iter / LAYOUT_ITER);
    const dx = new Array<number>(V).fill(0);
    const dy = new Array<number>(V).fill(0);

    // Repulsive force — all pairs.
    for (let i = 0; i < V; i++) {
      for (let j = i + 1; j < V; j++) {
        let ddx = vertices[i]!.x - vertices[j]!.x;
        let ddy = vertices[i]!.y - vertices[j]!.y;
        let dist = Math.sqrt(ddx * ddx + ddy * ddy);
        if (dist < 0.001) {
          // Coincident — nudge apart deterministically.
          ddx = 0.01;
          ddy = 0;
          dist = 0.01;
        }
        const force = (k * k) / dist;
        const ux = (ddx / dist) * force;
        const uy = (ddy / dist) * force;
        dx[i]! += ux;
        dy[i]! += uy;
        dx[j]! -= ux;
        dy[j]! -= uy;
      }
    }

    // Attractive force — only along edges.
    for (const e of edges) {
      const ddx = vertices[e.a]!.x - vertices[e.b]!.x;
      const ddy = vertices[e.a]!.y - vertices[e.b]!.y;
      const dist = Math.max(0.001, Math.sqrt(ddx * ddx + ddy * ddy));
      const force = (dist * dist) / k;
      const ux = (ddx / dist) * force;
      const uy = (ddy / dist) * force;
      dx[e.a]! -= ux;
      dy[e.a]! -= uy;
      dx[e.b]! += ux;
      dy[e.b]! += uy;
    }

    // Apply displacement with temperature cap, then clamp to canvas.
    for (let i = 0; i < V; i++) {
      const len = Math.sqrt(dx[i]! * dx[i]! + dy[i]! * dy[i]!);
      if (len > 0) {
        const cap = Math.min(len, temp);
        vertices[i]!.x += (dx[i]! / len) * cap;
        vertices[i]!.y += (dy[i]! / len) * cap;
      }
      vertices[i]!.x = Math.max(0.08, Math.min(0.92, vertices[i]!.x));
      vertices[i]!.y = Math.max(0.08, Math.min(0.92, vertices[i]!.y));
    }
  }

  return vertices;
}

function fallbackGraph(V: number): Edge[] {
  // Cycle: 0-1-2-...-V-1-0 — always connected, always Euler (0 odd vertices).
  const out: Edge[] = [];
  for (let i = 0; i < V; i++) out.push({ id: i, a: i, b: (i + 1) % V });
  return out;
}

function keyOf(a: number, b: number): string {
  return a < b ? `${a}-${b}` : `${b}-${a}`;
}

/**
 * Difficulty score — per GAME-SPEC §2.1.
 * Higher = harder.
 */
export function diffScore(vertices: Vertex[], edges: Edge[]): number {
  const V = vertices.length;
  const E = edges.length;
  const avgDeg = (2 * E) / V;
  const degs = new Array<number>(V).fill(0);
  for (const e of edges) {
    degs[e.a]! += 1;
    degs[e.b]! += 1;
  }
  const mean = degs.reduce((a, b) => a + b, 0) / V;
  const variance = degs.reduce((a, d) => a + (d - mean) ** 2, 0) / V;
  return E + 2 * avgDeg + 4 * Math.sqrt(variance);
}

/** Map a raw difficulty score to one of 5 rarity tiers. */
export function rarityOf(score: number): Rarity {
  if (score < 15) return 'common';
  if (score < 20) return 'uncommon';
  if (score < 25) return 'rare';
  if (score < 32) return 'epic';
  return 'legendary';
}

/** Mode for a given date (UTC weekday). */
export function modeForDate(d: Date): import('./types').Mode {
  const weekday = d.getUTCDay();
  switch (weekday) {
    case 1: return 'standard';
    case 2: return 'color_rule';
    case 3: return 'time_attack';
    case 4: return 'mirror';
    case 5: return 'inverse';
    case 6: return 'pair';
    default: return 'blind';
  }
}
